import { TranslateService } from "@ngx-translate/core";
import L from "../../leaflet/leaflet-plugins-loader";
import { ILeafletInstance } from "../../leaflet/leaflet.interface";
import {
  IMapWidgetFormGroupEntities,
  IMapWidgetFormGroupSettings,
} from "../../../map-widget-settings/map-widget-settings.interface";
import { INgsiAIS } from "../interfaces/ngsi-ais.interface";
import { TrackerDetails } from "../../leaflet/markers/leaflet-tracksymbol2/ais-track-symbol";
import { MarkerService } from "../../leaflet/markers/marker.service";
import { INgsiMaritimeTargets } from "../interfaces/ngsi-maritime-targets.interface";
import * as AisTrackSymbol from "../../leaflet/markers/leaflet-tracksymbol2/ais-track-symbol";
import * as NgsildConstants from "../helpers/ngsild.constants";
import { INgsiEntity } from "../interfaces/ngsi-entity.interface";
import { NgsiIdPrefix } from "../helpers/ngsild.constants";
import { INotification } from "ngsild-connector";
import { PopupService } from "../../leaflet/popup/popup.service";
import { TooltipService } from "../../leaflet/tooltips/tooltip.service";
import { IMapWidgetPermissions } from "../../../../shared/interfaces/map-widget-permission.interface";
import * as leafletInstanceConfig from "../helpers/leaflet-instance-config.constants";
import { INgsiRadarTarget } from "../interfaces/ngsi-radar-target.interface";

export const entityType: string = `AIS`;

export const getPopupNoData = (
  translator: TranslateService,
  latitude: number,
  longitude: number,
): ReturnType<typeof L.responsivePopup> => {
  return L.responsivePopup({
    hasTip: true,
    autoPan: false,
    offset: [15, 20],
    className: "osm-map-popup",
    closeOnClick: true,
    closeButton: true,
    autoClose: true,
    interactive: true,
  })
    .setLatLng([latitude, longitude])
    .setContent(`<strong>${translator.instant("MAP.AIS.NO_DATA")}</strong>`);
};

export const allowDisplayOfEntity = (
  settings: IMapWidgetFormGroupSettings,
  entity: INgsiEntity,
): boolean => {
  const dataSourceIdentifier = settings.dataSourceIdentifier;
  const mappedToTrackId =
    leafletInstanceConfig.config.dataSourceSelector.TrackID.label &&
    entity.id.includes(
      `${leafletInstanceConfig.config.dataSourceSelector.TrackID.value.owner}`,
    );
  const mappedToMMSI =
    dataSourceIdentifier ===
      leafletInstanceConfig.config.dataSourceSelector.MMSI.label &&
    entity.id.includes(
      `${leafletInstanceConfig.config.dataSourceSelector.MMSI.value.owner}`,
    );
  const mappedToDatasource = mappedToTrackId || mappedToMMSI;

  return entity.type === entityType && mappedToDatasource;
};

export const filterOnAISObjectDetection = (
  notification: INotification,
  appPluginWidgetSettings: IMapWidgetFormGroupSettings,
  widgetPermissions: IMapWidgetPermissions,
): boolean => {
  const settings = appPluginWidgetSettings;
  if (settings?.entities) {
    return (
      widgetPermissions.aisTargetsDataEnabled &&
      allowDisplayOfEntity(settings, notification.data[0])
    );
  } else {
    return false;
  }
};

export const isNotificationOfConfiguredEntity = (
  notification: INotification,
  settings: IMapWidgetFormGroupSettings,
): boolean => {
  const entity = notification.data[0];
  if (!settings.entities) {
    return false;
  }
  return settings.entities.some(
    (settingsEntity: IMapWidgetFormGroupEntities) =>
      entity.id === `${NgsiIdPrefix}${settingsEntity.id}`,
  );
};

export const createMarker = (
  entity: INgsiAIS,
  settings: IMapWidgetFormGroupSettings,
  instance: ILeafletInstance,
  popupService: PopupService,
  tooltipService: TooltipService,
  markerService: MarkerService,
): L.Marker<any> | undefined => {
  const trackerDetails = AisTrackSymbol.createMarker(entity.id, instance);
  if (!trackerDetails?.marker) {
    return undefined;
  }

  (trackerDetails.marker as any).bindContextMenu({
    contextmenu: false,
    contextmenuItems: [],
  });

  popupService.bindPopupOnce(
    trackerDetails.marker,
    entity,
    settings,
    instance,
    popupService,
    trackerDetails,
  );

  tooltipService.bindTooltipOnce(
    trackerDetails.marker,
    entity,
    instance.dataConfig.globale.showPermanentTooltip,
  );

  return trackerDetails.marker;
};

export const updateMarker = (
  entity: INgsiAIS,
  marker: L.Marker<any>,
  settings: IMapWidgetFormGroupSettings,
  instance: ILeafletInstance,
  popupService: PopupService,
  tooltipService: TooltipService,
  markerService: MarkerService,
): void => {
  const dataSourceTypeCategory =
    NgsildConstants.getSelectedDataSourceTypeCategory(
      settings.dataSourceTypeCategory,
    );
  if (entity.source === dataSourceTypeCategory) {
    updateAISMarkerPosition(
      entity,
      marker,
      settings,
      instance,
      popupService,
      tooltipService,
    );
  } else {
    updateAISMarkerHeading(
      entity,
      marker,
      settings,
      instance,
      popupService,
      tooltipService,
    );
  }
};

export const updateAISMarkerPosition = (
  entity: INgsiAIS | INgsiMaritimeTargets | INgsiRadarTarget,
  marker: L.Marker<any>,
  settings: IMapWidgetFormGroupSettings,
  instance: ILeafletInstance,
  popupService: PopupService,
  tooltipService: TooltipService,
) => {
  const trackerDetails = AisTrackSymbol.updatePositionReport(
    entity as INgsiAIS,
    marker,
    instance,
  );
  if (!trackerDetails?.marker) {
    console.log(`Tooltip und Popup nicht updatebar`);
    return;
  }

  tooltipService.bindTooltipOnce(
    trackerDetails.marker,
    entity,
    instance.dataConfig.globale.showPermanentTooltip,
  );
  tooltipService.updateTooltipContent(entity, trackerDetails.marker);

  popupService.bindPopupOnce(
    trackerDetails.marker,
    entity,
    settings,
    instance,
    popupService,
    trackerDetails,
  );
};

export const updateAISMarkerHeading = (
  entity: INgsiAIS,
  marker: L.Marker<any>,
  settings: IMapWidgetFormGroupSettings,
  instance: ILeafletInstance,
  popupService: PopupService,
  tooltipService: TooltipService,
): void => {
  const trackerDetails = AisTrackSymbol.updateMarkerHeading(
    entity,
    marker,
    instance,
  );

  if (!trackerDetails?.marker) {
    return;
  }

  tooltipService.bindTooltipOnce(
    trackerDetails.marker,
    entity,
    instance.dataConfig.globale.showPermanentTooltip,
  );
  tooltipService.updateTooltipContent(entity, trackerDetails.marker);

  popupService.bindPopupOnce(
    trackerDetails.marker,
    entity,
    settings,
    instance,
    popupService,
    trackerDetails,
  );
};
